/**
 * Zod schemas for runtime validation of HTTP request/response payloads.
 *
 * Every backend route uses these schemas to validate incoming requests via
 * middleware, and the frontend uses them (optionally) to validate responses.
 * This keeps the contract in one place — a single edit here propagates to
 * both sides.
 *
 * Pattern for adding a new endpoint:
 * 1. Define request and response schemas here
 * 2. Derive TypeScript types with z.infer where useful
 * 3. Import in backend route and call `schema.parse(req.body)`
 * 4. Import in frontend API client for typed calls
 */
import { z } from 'zod';
// -------------------------------------------------------------------------
// Reusable primitives
// -------------------------------------------------------------------------
export const EmailSchema = z.string().trim().toLowerCase().email();
export const UuidSchema = z.string().uuid();
export const IsoDateSchema = z.string().datetime();
export const NonEmptyStringSchema = z.string().trim().min(1);
export const ConfidenceSchema = z.number().min(0).max(1);
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    sortBy: z.string().optional(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
// -------------------------------------------------------------------------
// Auth — O365 callback and session
// -------------------------------------------------------------------------
export const O365CallbackSchema = z.object({
    code: NonEmptyStringSchema,
    state: NonEmptyStringSchema.optional(),
    codeVerifier: NonEmptyStringSchema.optional(),
    redirectUri: z.string().url(),
});
export const O365IdTokenSchema = z.object({
    idToken: NonEmptyStringSchema,
});
// -------------------------------------------------------------------------
// Access Manager — users
// -------------------------------------------------------------------------
export const CreateUserSchema = z.object({
    email: EmailSchema,
    displayName: NonEmptyStringSchema.max(200),
    roles: z.array(NonEmptyStringSchema).default([]),
    directPermissions: z.array(NonEmptyStringSchema).default([]),
});
export const UpdateUserSchema = z.object({
    displayName: NonEmptyStringSchema.max(200).optional(),
    isActive: z.boolean().optional(),
    roles: z.array(NonEmptyStringSchema).optional(),
    directPermissions: z.array(NonEmptyStringSchema).optional(),
});
// -------------------------------------------------------------------------
// Access Manager — roles
// -------------------------------------------------------------------------
export const RoleKeySchema = z
    .string()
    .trim()
    .min(2)
    .max(50)
    .regex(/^[a-z][a-z0-9_]*$/, 'Role key must be lowercase snake_case');
export const CreateRoleSchema = z.object({
    key: RoleKeySchema,
    name: NonEmptyStringSchema.max(100),
    description: z.string().max(500).default(''),
    permissions: z.array(NonEmptyStringSchema),
});
export const UpdateRoleSchema = z.object({
    name: NonEmptyStringSchema.max(100).optional(),
    description: z.string().max(500).optional(),
    permissions: z.array(NonEmptyStringSchema).optional(),
});
// -------------------------------------------------------------------------
// Access Manager — page registry
// -------------------------------------------------------------------------
export const RegisterPagesSchema = z.object({
    pages: z.array(z.object({
        path: z.string().trim().min(1).max(200).startsWith('/'),
        title: NonEmptyStringSchema.max(100),
        module: NonEmptyStringSchema,
        requiredPermission: NonEmptyStringSchema,
    })),
});
// -------------------------------------------------------------------------
// Ingestion
// -------------------------------------------------------------------------
export const UploadInitSchema = z.object({
    loanId: NonEmptyStringSchema.max(100),
    borrowerName: NonEmptyStringSchema.max(200),
    productCode: NonEmptyStringSchema.max(50),
    branchCode: NonEmptyStringSchema.max(50),
    officerId: z.string().max(100).nullable().optional(),
    fileName: NonEmptyStringSchema.max(300),
    fileSize: z.number().int().positive().max(500 * 1024 * 1024), // 500 MB cap
    mimeType: NonEmptyStringSchema,
});
export const UploadCompleteSchema = z.object({
    uploadId: UuidSchema,
    s3Key: NonEmptyStringSchema,
    eTag: NonEmptyStringSchema.optional(),
});
// -------------------------------------------------------------------------
// Masters — create/update record, create type, import/export
// -------------------------------------------------------------------------
export const CreateMasterTypeSchema = z.object({
    key: z
        .string()
        .trim()
        .min(2)
        .max(50)
        .regex(/^[a-z][a-z0-9_]*$/, 'Master type key must be lowercase snake_case'),
    name: NonEmptyStringSchema.max(100),
    description: z.string().max(500).default(''),
    schemaJson: z.record(z.unknown()),
});
export const UpsertMasterRecordSchema = z.object({
    masterTypeKey: NonEmptyStringSchema,
    recordKey: NonEmptyStringSchema.max(200),
    content: z.record(z.unknown()),
    changeReason: z.string().max(500).optional(),
});
export const UpdateMasterRecordSchema = z.object({
    content: z.record(z.unknown()),
    isActive: z.boolean().optional(),
    changeReason: z.string().max(500).optional(),
});
export const RollbackMasterRecordSchema = z.object({
    targetVersion: z.number().int().min(1),
    changeReason: z.string().max(500),
});
export const ImportMasterRecordsSchema = z.object({
    masterTypeKey: NonEmptyStringSchema,
    records: z
        .array(z.object({
        recordKey: NonEmptyStringSchema.max(200),
        content: z.record(z.unknown()),
    }))
        .min(1)
        .max(500),
    changeReason: z.string().max(500).optional(),
});
// -------------------------------------------------------------------------
// Admin config editor
// -------------------------------------------------------------------------
export const UpsertConfigSchema = z.object({
    key: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .regex(/^[a-z][a-z0-9_.]*$/, 'Config key must be lowercase dotted snake_case'),
    valueJson: z.unknown(),
    description: z.string().max(500).default(''),
});
// -------------------------------------------------------------------------
// Notifications
// -------------------------------------------------------------------------
export const SendNotificationSchema = z.object({
    recipients: z
        .union([
        z.literal('all'),
        z.array(z.string().uuid()),
    ]),
    source: NonEmptyStringSchema.max(50),
    severity: z.enum(['info', 'warning', 'critical']).default('info'),
    title: NonEmptyStringSchema.max(200),
    body: z.string().max(2000).optional(),
    linkPath: z.string().max(500).optional(),
    details: z.record(z.unknown()).optional(),
});
export const UpdateNotificationPrefsSchema = z.object({
    emailOnCritical: z.boolean().optional(),
    emailOnWarning: z.boolean().optional(),
    digestEnabled: z.boolean().optional(),
    digestTimeOfDay: z
        .string()
        .regex(/^[0-2]\d:[0-5]\d$/, 'Expected HH:MM')
        .optional(),
});
export const MarkNotificationReadSchema = z.object({
    ids: z.array(UuidSchema).min(1).max(200),
});
// -------------------------------------------------------------------------
// Ingestion — create loan file, register document, submit, API push
// -------------------------------------------------------------------------
export const CreateLoanFileSchema = z.object({
    loanNumber: NonEmptyStringSchema.max(64),
    borrowerName: NonEmptyStringSchema.max(200),
    productCode: NonEmptyStringSchema.max(64),
    branchCode: NonEmptyStringSchema.max(64),
    officerId: z.string().trim().max(64).optional(),
    // Documents to reserve upload slots for (count only or with filenames).
    // The server returns a presigned URL per reserved slot.
    documents: z
        .array(z.object({
        originalFileName: NonEmptyStringSchema.max(300),
        mimeType: NonEmptyStringSchema.max(100),
        fileSizeBytes: z.number().int().min(1),
    }))
        .min(1)
        .max(40),
    metadata: z.record(z.unknown()).optional(),
});
/** Register a document AFTER the client uploaded to the presigned S3 URL. */
export const RegisterDocumentUploadedSchema = z.object({
    documentId: UuidSchema,
    sha256Hex: z
        .string()
        .regex(/^[a-f0-9]{64}$/, 'Expected 64-char lowercase hex SHA-256'),
});
/** Submit a loan file for processing (draft → received). */
export const SubmitLoanFileSchema = z.object({
    notes: z.string().max(1000).optional(),
});
export const RejectLoanFileSchema = z.object({
    reason: NonEmptyStringSchema.max(1000),
});
/**
 * API push from an upstream LOS/DMS. The caller has already uploaded the
 * documents to our S3 bucket via their own presigned URL request; we
 * trust the HMAC on this request and register everything.
 */
export const ApiPushSchema = z.object({
    connectorKey: NonEmptyStringSchema.max(64),
    loanNumber: NonEmptyStringSchema.max(64),
    borrowerName: NonEmptyStringSchema.max(200),
    productCode: NonEmptyStringSchema.max(64),
    branchCode: NonEmptyStringSchema.max(64),
    officerId: z.string().trim().max(64).optional(),
    documents: z
        .array(z.object({
        originalFileName: NonEmptyStringSchema.max(300),
        mimeType: NonEmptyStringSchema.max(100),
        fileSizeBytes: z.number().int().min(1),
        s3Key: NonEmptyStringSchema.max(500),
        sha256Hex: z
            .string()
            .regex(/^[a-f0-9]{64}$/, 'Expected 64-char lowercase hex SHA-256'),
    }))
        .min(1)
        .max(40),
    metadata: z.record(z.unknown()).optional(),
});
/** Loan file list filter query — used by /api/ingestion/loan-files GET */
export const LoanFileFilterSchema = PaginationQuerySchema.extend({
    state: z.string().optional(),
    productCode: z.string().optional(),
    branchCode: z.string().optional(),
    search: z.string().trim().optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
// -------------------------------------------------------------------------
// Finding status changes
// -------------------------------------------------------------------------
export const OverrideFindingSchema = z.object({
    findingId: UuidSchema,
    newStatus: z.enum(['under_review', 'accepted', 'rejected', 'compensated']),
    reason: NonEmptyStringSchema.max(2000),
});
// -------------------------------------------------------------------------
// Parsing — review-queue actions and manual retriggers (Sprint 5)
// -------------------------------------------------------------------------
export const ResolveReviewItemSchema = z.object({
    action: z.enum(['approve', 'correct', 'reject']),
    /** Only when action=correct: the corrected doc type OR the corrected fields object */
    correctedType: NonEmptyStringSchema.max(100).optional(),
    correctedFields: z.record(z.unknown()).optional(),
    notes: z.string().trim().max(4000).optional(),
});
export const ReviewQueueFilterSchema = PaginationQuerySchema.extend({
    itemType: z.enum(['classification', 'extraction']).optional(),
    status: z.enum(['pending', 'approved', 'corrected', 'rejected', 'not_required']).optional(),
    loanFileId: UuidSchema.optional(),
    assignedTo: UuidSchema.optional(),
});
export const AssignReviewItemSchema = z.object({
    assigneeUserId: UuidSchema.nullable(),
});
export const RetriggerParseSchema = z.object({
    stage: z.enum(['ocr', 'classify', 'extract', 'all']).default('all'),
    reason: z.string().trim().max(2000).optional(),
});
/** Manual classification override by an auditor with parsing.override_classification */
export const OverrideClassificationSchema = z.object({
    newType: NonEmptyStringSchema.max(100),
    reason: NonEmptyStringSchema.max(2000),
});
// -------------------------------------------------------------------------
// Validation (Sprint 6) — run trigger + finding filter + resolve
// -------------------------------------------------------------------------
/** Trigger a validation run. Omit ucIds for all UCs. */
export const RunValidationSchema = z.object({
    ucIds: z
        .array(z.enum(['uc1', 'uc2', 'uc3']))
        .max(3)
        .optional(),
    llmEnabled: z.boolean().optional(),
    reason: z.string().trim().max(2000).optional(),
});
/** Filter the findings list across all loan files. */
export const FindingFilterSchema = z
    .object({
    loanFileId: UuidSchema.optional(),
    ucId: z.enum(['uc1', 'uc2', 'uc3']).optional(),
    ruleId: z.string().trim().max(200).optional(),
    status: z
        .enum(['open', 'accepted', 'waived', 'false_positive', 'superseded'])
        .optional(),
    severity: z.enum(['critical', 'high', 'medium', 'low', 'info']).optional(),
    verdictSource: z
        .enum(['deterministic', 'llm', 'both_agree', 'both_disagree'])
        .optional(),
    /** Only show the latest run per loan file. Default true. */
    latestOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(true)
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true')),
    /** Sort on loan number first so each loan's findings stay contiguous. */
    groupByLoan: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => (typeof v === 'string' ? v === 'true' : v)),
})
    .merge(PaginationQuerySchema);
/** Resolve a finding with an action + optional notes. */
export const ResolveFindingSchema = z.object({
    action: z.enum(['accept', 'waive', 'false_positive']),
    notes: z.string().trim().max(2000).optional(),
});
//# sourceMappingURL=index.js.map